import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Header, Footer } from "../components/Orchestrio";
import { API } from "../App";
import { trackContactSubmit } from "../utils/analytics";
import SEO from "../components/SEO";

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', 
    email: '', 
    phone: '', 
    company: '', 
    message: '', 
    gdpr_consent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.gdpr_consent) {
      toast.error('Please agree to the privacy policy to continue.');
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(`${API}/public/contact`, formData);
      trackContactSubmit('contact', 'contact_page');
      toast.success(response.data.message || 'Message sent. We\'ll be in touch within 24 hours.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '', gdpr_consent: false });
    } catch (error) {
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="contact-page">
      <SEO 
        title="Contact"
        description="Get in touch with Orchestrio. We design and build digital systems for UK businesses."
      />
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#5F8FB8] text-sm font-medium tracking-wide mb-6">
            Contact
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Get in touch
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-xl leading-relaxed">
            Tell us about your project. We respond to all enquiries within 24 hours.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6 pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="name">
                      Name <span className="text-[#5F8FB8]">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="email">
                      Email <span className="text-[#5F8FB8]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="phone">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="+44..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="company">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Company name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="message">
                    Tell us about your project <span className="text-[#5F8FB8]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="input-field resize-none"
                    placeholder="What are you building? What problem are you trying to solve? Any timeline or budget constraints?"
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="gdpr_consent"
                    name="gdpr_consent"
                    checked={formData.gdpr_consent}
                    onChange={handleChange}
                    className="mt-1"
                  />
                  <label htmlFor="gdpr_consent" className="text-sm text-[#9CA3AF]">
                    I agree to Orchestrio storing my details to respond to this enquiry. 
                    See our <Link to="/privacy" className="text-[#5F8FB8] hover:underline">privacy policy</Link>.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="submit-contact-form"
                >
                  {loading ? "Sending..." : "Send message"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="font-semibold mb-4">Email</h3>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#5F8FB8] mt-0.5" />
                  <a href="mailto:hello@orchestrio.co.uk" className="text-[#F4F6F8] hover:text-[#5F8FB8] text-sm">
                    hello@orchestrio.co.uk
                  </a>
                </div>
              </div>

              <div className="pt-6 border-t border-[#2E2E2E]">
                <h3 className="font-semibold mb-3">Response time</h3>
                <p className="text-sm text-[#9CA3AF]">
                  We respond to all enquiries within 24 hours during business days.
                </p>
              </div>

              <div className="pt-6 border-t border-[#2E2E2E]">
                <h3 className="font-semibold mb-3">Working hours</h3>
                <p className="text-sm text-[#9CA3AF]">
                  Monday – Friday<br />
                  9:00 – 18:00 GMT
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
